import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Switch,
  Spinner,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateBotTemplate from "./CreateBotTemplate";
import TemplateContentEditor from "./TemplateContentEditor";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";

const BOT_TEMPLATES_PAGE_ID = PAGE_IDS.BOT_TEMPLATES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const ListBotTemplate = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "lg" });

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editableTemplate, setEditableTemplate] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === BOT_TEMPLATES_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/bot/template?page_id=${BOT_TEMPLATES_PAGE_ID}`);
      setTemplates(response?.data || []);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch templates."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreated = () => {
    setShowCreateModal(false);
    fetchTemplates();
  };

  const handleEdit = (item) => {
    setEditErrors({});
    setEditableTemplate({ ...item, platform: item.platform || "" });
  };

  const handleSave = async () => {
    const errors = {};
    if (!editableTemplate.key.trim()) errors.key = t("Key is required");
    else if (!/^[a-z0-9_]+$/.test(editableTemplate.key.trim())) errors.key = t("Lowercase letters, numbers, and underscores only");
    if (!editableTemplate.content.trim()) errors.content = t("Content is required");
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        page_id: BOT_TEMPLATES_PAGE_ID,
        templateId: editableTemplate.id,
        key: editableTemplate.key.trim(),
        platform: editableTemplate.platform || null,
        content: editableTemplate.content.trim(),
        isActive: editableTemplate.isActive,
      };
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/bot/template`, "PUT", data);
      if (!response) {
        throw new Error("Failed to update template");
      }
      await fetchTemplates();
      setEditableTemplate(null);
      toast({
        title: t("Template updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update template."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/template`,
        "DELETE",
        { page_id: BOT_TEMPLATES_PAGE_ID, templateId }
      );
      if (!response) {
        throw new Error("Failed to delete template");
      }
      setTemplates((prev) => prev.filter((r) => r.id !== templateId));
      toast({
        title: t("Template deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to delete template."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/template`,
        "PUT",
        { page_id: BOT_TEMPLATES_PAGE_ID, templateId: item.id, isActive: !item.isActive }
      );
      if (!response) {
        throw new Error("Failed to update template");
      }
      setTemplates((prev) => prev.map((r) => (r.id === item.id ? { ...r, isActive: !r.isActive } : r)));
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to toggle template status."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const filteredTemplates = templates.filter((r) => (r.key || "").toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Bot Templates")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add Template")}
          </Button>
        </Flex>
        <Input
          placeholder={t("Search by key")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "220px" }}
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
        />
      </Flex>
      <Text fontSize="xs" color="gray.500" px={{ base: "10px", md: "25px" }} mb="16px">
        {t('Controls the wording of broadcast messages. Use key "broadcast_default" for the normal fallback and "broadcast_breaking" for breaking news; scope to a platform or leave "Both" for a global template.')}
      </Text>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Key")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Platform")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Content")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredTemplates.map((item) => (
                <Tr key={item.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td fontFamily="mono">{item.key}</Td>
                  <Td>{item.platform ? t(item.platform) : t("Both")}</Td>
                  <Td maxW="280px">
                    <Text noOfLines={1} fontSize="sm" color="gray.600">{item.content}</Text>
                  </Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={item.isActive ? "green" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                        {item.isActive ? t("Active") : t("Inactive")}
                      </Badge>
                      <Switch
                        size="sm"
                        isChecked={item.isActive}
                        isDisabled={!permissions.canEdit}
                        onChange={() => handleToggleActive(item)}
                        colorScheme="brand"
                      />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEdit(item)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        isDisabled={!permissions.canDelete}
                        onClick={() => handleDelete(item.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {editableTemplate && (
        <Modal isOpen={true} onClose={() => setEditableTemplate(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Template")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box>
                <Text fontSize="sm" color="gray.600" mb="2">{t("Key")}</Text>
                <Input
                  value={editableTemplate.key}
                  onChange={(e) => setEditableTemplate({ ...editableTemplate, key: e.target.value })}
                  fontFamily="mono"
                  {...fieldProps}
                  isInvalid={!!editErrors.key}
                />
                {editErrors.key && <Text color="red.500" fontSize="xs" mt="4px">{editErrors.key}</Text>}
              </Box>

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Platform")}</Text>
                <Select
                  value={editableTemplate.platform}
                  onChange={(e) => setEditableTemplate({ ...editableTemplate, platform: e.target.value })}
                  {...fieldProps}
                >
                  <option value="">{t("Both")}</option>
                  <option value="TELEGRAM">{t("Telegram")}</option>
                  <option value="WHATSAPP">{t("WhatsApp")}</option>
                </Select>
              </Box>

              <TemplateContentEditor
                value={editableTemplate.content}
                onChange={(content) => setEditableTemplate({ ...editableTemplate, content })}
                isInvalid={!!editErrors.content}
                errorMessage={editErrors.content}
              />
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setEditableTemplate(null)}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create Template")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateBotTemplate onCreated={handleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ListBotTemplate;
